import { ValueObject } from './value-object';

/**
 * Text Value Object
 *
 * Represents a validated string value with utility methods for text manipulation.
 * Extends the ValueObject base class to provide immutability and value semantics.
 *
 * @example
 * ```typescript
 * const text = new Text('Hello World');
 * console.log(text.toString()); // 'Hello World'
 * console.log(text.toSlug()); // 'hello-world'
 * ```
 */
export class Text extends ValueObject<string> {
  /**
   * Creates a new Text instance.
   *
   * @param text - The string value to be wrapped in the Text value object
   * @throws {Error} Throws 'Not a valid Text' if the input is not a string
   *
   * @example
   * ```typescript
   * const text = new Text('Sample text');
   * ```
   */
  constructor(text: string) {
    const isValid = Text.validate(text);
    if (!isValid) throw new Error('Not a valid Text');

    super(text);
  }

  /**
   * Validates if the provided input is a valid string.
   *
   * @param text - The value to validate
   * @returns true if the value is a string, false otherwise
   *
   * @example
   * ```typescript
   * Text.validate('hello'); // true
   * Text.validate(123); // false
   * Text.validate(null); // false
   * ```
   */
  static validate(text: string): boolean {
    const isString = typeof text === 'string';
    return isString;
  }

  /**
   * Factory method to create a new Text instance.
   *
   * @param input - The string value to be wrapped in the Text value object
   * @returns A new Text instance
   * @throws {Error} Throws 'Not a valid Text' if the input is not a string
   *
   * @example
   * ```typescript
   * const text = Text.create('Sample text');
   * ```
   */
  static create(input: string): Text {
    return new Text(input);
  }

  /**
   * Returns the string representation of the Text value object.
   *
   * @returns The underlying string value
   *
   * @example
   * ```typescript
   * const text = new Text('Hello');
   * console.log(text.toString()); // 'Hello'
   * ```
   */
  toString(): string {
    return this.value;
  }

  /**
   * Converts the text to a URL-friendly slug format.
   *
   * The transformation process:
   * 1. Converts to lowercase
   * 2. Normalizes Unicode characters (NFD form)
   * 3. Removes diacritical marks (accents)
   * 4. Removes all non-alphanumeric characters
   *
   * @returns A slug version of the text (lowercase alphanumeric only)
   *
   * @example
   * ```typescript
   * const text = new Text('Hello World!');
   * console.log(text.toSlug()); // 'helloworld'
   *
   * const text2 = new Text('Café José');
   * console.log(text2.toSlug()); // 'cafejose'
   *
   * const text3 = new Text('  Multiple   Spaces  ');
   * console.log(text3.toSlug()); // 'multiplespaces'
   *
   * const text4 = new Text('Product-123');
   * console.log(text4.toSlug()); // 'product123'
   * ```
   */
  toSlug(): string {
    return this.value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }
}
